<template>
  <div class="layout">
    <Button
      class="skip-link"
      label="Ga naar hoofdinhoud"
      style="position: absolute; transform: translateY(-200%) scale(0.75)"
      @click="handleSkipLink"
    />
    <LazyPageLoadingBar v-if="!isErrorLayout" />
    <PageTopBar :is-error-layout="isErrorLayout" />
    <main
      id="main-content"
      ref="mainRef"
      class="main"
      aria-label="hoofdinhoud"
      tabindex="-1"
    >
      <slot />
    </main>

    <LazyPageFooter :is-error-layout="isErrorLayout" />
    <LazyPageSnackBar />

    <LazyPageChatButton v-if="showChatBtn" />

    <ClientOnly v-if="computedDevModeOrDevEnv">
      <LazyDevActionsButton />
    </ClientOnly>
    <ClientOnly v-if="!computedDevModeOrDevEnv">
      <LazyPageEnvironmentNotification />
    </ClientOnly>

    <LazyPromoModal
      v-if="hasModal"
      :visible="showModal"
      :data="modalData"
      @close-modal="closeModal"
    />
    <LazyShortsModalSingle />
  </div>
</template>

<script setup lang="ts">
import usePageStore from '~/stores/page';
import { usePromoModal } from '~/composables/usePromoModal';
import useGlobalConfigStore from '~/stores/global';

const pageStore = usePageStore();

const route = useRoute();
const mainRef = ref<HTMLElement | undefined>();
const showChatBtn = ref<boolean>(false);
const hasModal = ref<boolean>(false);

defineProps({
  isErrorLayout: {
    type: Boolean,
    default: false,
  },
});

const globalConfigModal = useGlobalConfigStore().modal || [];
const modalData = globalConfigModal[0] || {};

hasModal.value = !!globalConfigModal[0];

const state = reactive({
  isArticle: false,
});

onMounted(() => {
  state.isArticle = pageStore.isArticle;

  pageStore.$subscribe(() => {
    state.isArticle = pageStore.isArticle;
  });
});

const { showModal, closeModal, startTracking, resetScrollTracking, modalId } = usePromoModal();

if (hasModal.value) {
  modalId.value = parseInt(modalData.id + '', 10);
  const pageViews = useState('promo-page-views', () => 0);

  watch(
    () => route.fullPath,
    () => {
      resetScrollTracking(); // Always reset when navigating to a new page

      pageViews.value++;

      if (pageViews.value === 1 && import.meta.client) {
        startTracking();
      }
    },
  );
}

const computedDevModeOrDevEnv = computed(() => {
  const { isEnv } = useUrl();
  return import.meta.dev || (import.meta.client && isEnv('dev'));
});

const shouldLoadChatBtn = () => {
  const { hasChatContent } = usePageStore();
  const firstBit = route.path.split('/')[1];
  const isNotChatPage = firstBit !== 'chat';
  showChatBtn.value = !hasChatContent && isNotChatPage;
};

watch(
  () => route.path,
  () => {
    shouldLoadChatBtn();
  },
  { immediate: true },
);

pageStore.$subscribe(() => {
  shouldLoadChatBtn();
});

function handleSkipLink() {
  const target = document.getElementById('main-content');
  if (target) {
    target.focus();
    target.scrollIntoView({ behavior: 'smooth' });
  }
}

if (import.meta.client) {
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      console.log('Pageshow (Safari BFCache issue');
      window.location.reload();
    }
  });

  window.addEventListener('pagehide', () => {
    console.log('pagehide');
    // Prevent BFCache
  });
}
</script>

<style lang="postcss" scoped>
.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  justify-items: stretch;
  align-items: stretch;
}

.main {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  opacity: 1;
}

.skip-link {
  /* this button has some inline styling to prevent content shift */
  position: absolute;
  top: 0;
  left: 0.5rem;

  z-index: 1000;
  transform-origin: top left;
  transition: transform 0.2s ease;
}

.skip-link:focus {
  transform: translateY(0.5rem) scale(0.75) !important;
}
</style>
